import { Injectable } from '@visiba/instantiation';
import type {
	CallStatusApiModel,
	CancelBookingApiResult,
	ClientAppointmentApiModel,
	FileUploadApiModel,
	SurveyApiModel,
	SurveyQuestionAnswerApiModel,
} from '@api_patient/generated/clientv2/models';
import { HttpResponse, HttpService, VisibaHttpOption } from '@application/http.service';

type MimeType =
	| 'application/msword'
	| 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
	| 'application/octet-stream';

@Injectable()
export class AppointmentApiService {
	constructor(private readonly httpService: HttpService) {
		// Empty
	}

	public async get(ticket: string, option?: VisibaHttpOption): HttpResponse<ClientAppointmentApiModel> {
		return this.httpService.get(`/Client/v2/appointments/${ticket}`, option);
	}

	public async getUpcomingAndUnfinished(): HttpResponse<ClientAppointmentApiModel[]> {
		return this.httpService.get(`/Client/v2/appointments`);
	}

	public getFileIdentifier(ticket: Guid, name: string, length: number, type: string): HttpResponse<FileUploadApiModel> {
		const sendData = {
			Name: name,
			Length: length,
			Type: type === '' ? this.resolveMimeType(name) : type,
		};

		return this.httpService.post(`Client/v2/appointments/${ticket}/files`, sendData);
	}

	public async cancel(ticket: string): HttpResponse<CancelBookingApiResult> {
		return this.httpService.delete(`/Client/v2/appointments/${ticket}`);
	}

	public async decline(bookingId: number): HttpResponse<ClientAppointmentApiModel> {
		return this.httpService.delete(`/Client/v2/appointments/decline/${bookingId.toString()}`);
	}

	public uploadChatFile(uploadURL: string, data: FormData): HttpResponse<undefined> {
		return this.httpService.post(`${uploadURL}`, data);
	}

	public callStatus(ticket: string): HttpResponse<CallStatusApiModel> {
		return this.httpService.get(`/Client/v2/appointments/${ticket}/callstatus`);
	}

	public survey(unitId: number): HttpResponse<SurveyApiModel> {
		return this.httpService.get(`/client/v2/units/${unitId}/surveys/callCompleted`);
	}

	public surveyAnswers(
		unitId: number,
		data: {
			Answers: SurveyQuestionAnswerApiModel[];
			AppointmentTicket: string;
		},
	): HttpResponse<unknown> {
		return this.httpService.post(`/client/v2/units/${unitId}/surveys/callCompleted`, data);
	}

	public downloadChatFile(downloadURL: string): HttpResponse<Blob> {
		// const options = new VisibaOptions({
		// 	requestConfig: {
		// 		responseType: 'blob',
		// 		withCredentials: true,
		// 	},
		// });

		return this.httpService.get(`${downloadURL}`);
	}

	public deleteFile(deleteURL: string): HttpResponse<unknown> {
		// const options = new VisibaOptions({
		// 	requestConfig: {
		// 		withCredentials: true,
		// 	},
		// });

		return this.httpService.delete(`${deleteURL}`);
	}

	private resolveMimeType(fileName: string): MimeType {
		const extension = fileName.split('.')[fileName.split('.').length];
		switch (extension) {
			case 'doc':
				return 'application/msword';
			case 'docx':
				return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
			default:
				return 'application/octet-stream';
		}
	}
}
